import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Instructions = () => {
  const [selectedPlatform, setSelectedPlatform] = useState('Cloudflare Pages');
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (platform, section) => {
    setExpandedSections(prev => ({
      ...prev,
      [`${platform}-${section}`]: !prev[`${platform}-${section}`]
    }));
  };

  const platforms = {
    'Cloudflare Pages': {
      icon: FiIcons.FiCloud,
      color: 'orange',
      sections: {
        'Initial Setup': {
          steps: [
            'Sign up for a Cloudflare account at https://dash.cloudflare.com',
            'Go to Pages section in the left sidebar',
            'Click "Create a project" button',
            'Connect your GitHub account when prompted',
            'Select the repository you want to deploy'
          ]
        },
        'Build Configuration': {
          steps: [
            'Framework preset: Select "Vite" or "None"',
            'Build command: npm run build',
            'Build output directory: dist',
            'Node version: 20 (set via .nvmrc file)',
            'Environment variables: Add any VITE_ prefixed variables'
          ]
        },
        'DNS & Domain': {
          steps: [
            'After first deployment, go to project settings',
            'Click "Custom domains" to add your domain',
            'Update your DNS records as instructed',
            'Wait for SSL certificate provisioning (usually 5-10 minutes)',
            'Test your custom domain'
          ]
        },
        'Functions Setup': {
          steps: [
            'Create /functions/api folder structure',
            'Add TypeScript/JavaScript files with PagesFunction signature',
            'Functions will be auto-deployed at /api/ endpoint',
            'Test functions locally with `wrangler pages dev`',
            'Monitor function logs in Cloudflare dashboard'
          ]
        }
      }
    },
    'Netlify': {
      icon: FiIcons.FiGlobe,
      color: 'teal',
      sections: {
        'Initial Setup': {
          steps: [
            'Sign up for Netlify at https://app.netlify.com',
            'Click "New site from Git" button',
            'Select GitHub as Git provider',
            'Authorize Netlify to access your repositories',
            'Choose the repository to deploy'
          ]
        },
        'Build Configuration': {
          steps: [
            'Build command: npm run build',
            'Publish directory: dist',
            'Add netlify.toml file for advanced configuration',
            'Set environment variables in Site settings → Environment',
            'Configure Node version in netlify.toml or environment variables'
          ]
        },
        'Functions Setup': {
          steps: [
            'Create netlify/functions directory',
            'Add JavaScript files with handler exports',
            'Functions will be available at /.netlify/functions/',
            'Test locally with Netlify CLI: netlify dev',
            'Monitor function logs in Netlify dashboard'
          ]
        },
        'Forms & Redirects': {
          steps: [
            'Add data-netlify="true" to HTML forms',
            'Configure form notifications in Site settings → Forms',
            'Add redirects in netlify.toml file',
            'Use _redirects file for simple redirects',
            'Test form submissions and redirects'
          ]
        }
      }
    },
    'Supabase': {
      icon: FiIcons.FiDatabase,
      color: 'green',
      sections: {
        'Project Setup': {
          steps: [
            'Go to https://supabase.com and sign up',
            'Click "New Project" button',
            'Choose your organization or create one',
            'Set project name and database password',
            'Select region closest to your users',
            'Wait for project provisioning (1-2 minutes)'
          ]
        },
        'Authentication': {
          steps: [
            'Go to Authentication → Settings in Supabase dashboard',
            'Add your site URL to Site URL field',
            'Add redirect URLs for OAuth providers',
            'Enable email authentication by default',
            'Configure email templates if needed',
            'Test authentication flow'
          ]
        },
        'Database Schema': {
          steps: [
            'Use Table Editor to create tables',
            'Always enable Row Level Security (RLS)',
            'Create policies for each table',
            'Add foreign key relationships',
            'Set default values for columns',
            'Test database operations with SQL Editor'
          ]
        },
        'Client Integration': {
          steps: [
            'Install @supabase/supabase-js package',
            'Create supabase client instance',
            'Add environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY',
            'Set up authentication state management',
            'Implement CRUD operations',
            'Handle real-time subscriptions if needed'
          ]
        }
      }
    },
    'Dropbox': {
      icon: FiIcons.FiHardDrive,
      color: 'blue',
      sections: {
        'App Creation': {
          steps: [
            'Go to https://www.dropbox.com/developers',
            'Click "Create app" button',
            'Choose "Scoped access" option',
            'Select "Full Dropbox" or "App folder" access',
            'Name your app and agree to terms',
            'Save your App key and App secret'
          ]
        },
        'OAuth Setup': {
          steps: [
            'In app settings, go to "Permissions" tab',
            'Add required scopes (files.content.write, files.content.read)',
            'Go to "Settings" tab and add redirect URIs',
            'Enable "Additional users" if needed',
            'Test OAuth flow with provided URL',
            'Save access tokens securely'
          ]
        },
        'API Integration': {
          steps: [
            'Install Dropbox SDK: npm install dropbox',
            'Initialize Dropbox client with access token',
            'Implement file upload functionality',
            'Add file download and listing features',
            'Handle rate limits and errors',
            'Test with various file types and sizes'
          ]
        }
      }
    },
    'Google Drive': {
      icon: FiIcons.FiFolder,
      color: 'yellow',
      sections: {
        'Project Setup': {
          steps: [
            'Go to Google Cloud Console',
            'Create new project or select existing',
            'Enable Google Drive API',
            'Go to "Credentials" section',
            'Create OAuth 2.0 Client ID',
            'Configure consent screen with app details'
          ]
        },
        'Service Account': {
          steps: [
            'Create service account key',
            'Download JSON credentials file',
            'Share Google Drive folders with service account email',
            'Set appropriate permissions (view, edit)',
            'Store credentials securely',
            'Test API access'
          ]
        },
        'API Integration': {
          steps: [
            'Install Google APIs client: npm install googleapis',
            'Initialize Drive API with credentials',
            'Implement file upload with resumable upload',
            'Add folder creation and file organization',
            'Handle large files with chunked uploads',
            'Implement search and filtering'
          ]
        }
      }
    }
  };

  const currentPlatform = platforms[selectedPlatform];

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4">Platform Setup Instructions</h1>
        <p className="text-gray-600">Step-by-step guides for configuring each platform</p>
      </div>

      {/* Platform Selector */}
      <div className="mb-8">
        <div className="flex flex-wrap justify-center gap-3">
          {Object.keys(platforms).map(platform => {
            const Icon = platforms[platform].icon;
            return (
              <button
                key={platform}
                onClick={() => setSelectedPlatform(platform)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all ${
                  selectedPlatform === platform
                    ? `bg-${platforms[platform].color}-500 text-white shadow-lg scale-105`
                    : 'bg-white border hover:shadow-md'
                }`}
              >
                <SafeIcon icon={Icon} className="text-xl" />
                <span className="font-medium">{platform}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Instructions for Selected Platform */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <SafeIcon 
                icon={currentPlatform.icon} 
                className={`mr-2 text-${currentPlatform.color}-500`}
              />
              {selectedPlatform}
            </h2>
            <div className="space-y-2">
              {Object.keys(currentPlatform.sections).map(section => (
                <button
                  key={section}
                  onClick={() => toggleSection(selectedPlatform, section)}
                  className={`w-full text-left px-3 py-2 rounded transition-colors ${
                    expandedSections[`${selectedPlatform}-${section}`]
                      ? 'bg-blue-100 text-blue-700'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{section}</span>
                    <SafeIcon 
                      icon={expandedSections[`${selectedPlatform}-${section}`] ? FiIcons.FiChevronUp : FiIcons.FiChevronDown}
                      className="text-xs"
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {Object.entries(currentPlatform.sections).map(([sectionTitle, section]) => (
            <div 
              key={sectionTitle}
              className={`bg-white rounded-lg shadow-md overflow-hidden transition-all ${
                expandedSections[`${selectedPlatform}-${sectionTitle}`] ? 'max-h-96' : 'max-h-16'
              }`}
            >
              <button
                onClick={() => toggleSection(selectedPlatform, sectionTitle)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50"
              >
                <h3 className="text-lg font-semibold">{sectionTitle}</h3>
                <SafeIcon 
                  icon={expandedSections[`${selectedPlatform}-${sectionTitle}`] ? FiIcons.FiChevronUp : FiIcons.FiChevronDown}
                />
              </button>
              
              {expandedSections[`${selectedPlatform}-${sectionTitle}`] && (
                <div className="px-6 pb-6">
                  <ol className="space-y-3">
                    {section.steps.map((step, index) => (
                      <li key={index} className="flex">
                        <span className={`flex-shrink-0 w-8 h-8 bg-${currentPlatform.color}-100 text-${currentPlatform.color}-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3`}>
                          {index + 1}
                        </span>
                        <span className="text-gray-700 pt-1">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          ))}

          {/* Quick Tips */}
          <div className="bg-blue-50 border-l-4 border-blue-400 p-6">
            <h3 className="font-semibold text-blue-900 mb-3">Quick Tips</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="mr-2 mt-0.5 flex-shrink-0" />
                Always test in a development environment before production
              </li>
              <li className="flex items-start">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="mr-2 mt-0.5 flex-shrink-0" />
                Keep your API keys and credentials secure and never commit them to git
              </li>
              <li className="flex items-start">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="mr-2 mt-0.5 flex-shrink-0" />
                Monitor logs and usage metrics regularly
              </li>
              <li className="flex items-start">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="mr-2 mt-0.5 flex-shrink-0" />
                Set up alerts for important events and errors
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="font-semibold mb-3">Additional Resources</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <a 
                href="#"
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
              >
                <SafeIcon icon={FiIcons.FiExternalLink} />
                <span>Official Documentation</span>
              </a>
              <a 
                href="#"
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
              >
                <SafeIcon icon={FiIcons.FiExternalLink} />
                <span>API Reference</span>
              </a>
              <a 
                href="#"
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
              >
                <SafeIcon icon={FiIcons.FiExternalLink} />
                <span>Community Forum</span>
              </a>
              <a 
                href="#"
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
              >
                <SafeIcon icon={FiIcons.FiExternalLink} />
                <span>Video Tutorials</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Instructions;